<template>
  <!-- Cost table list -->
  <div class="invoice-import-costs" :class="{ hiddentable: !displayTable }">
    <div class="import-filters">

      <FilterBar 
        :cookieName="filterCookieName"
        filterBarType="costs" 
        @change="filtersChanged" 
        :mainFilters="['DateRangeFilter', 'ImportCustomerFilter', 'CostTypeFilter']"
        class="w-100"
      >
        <template v-slot:right-bar>
          <div class="" style="display:flex; gap: 0.5rem; align-items: center;">
            {{ $gettext('Aggregate items?') }}

            <font-awesome-icon id="tooltip-aggregate" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
            <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-aggregate" triggers="hover">
              <div class="tooltip-title">
                {{ $gettext('You can aggregate your costs in the following ways:') }}
              </div>
              <div class="tooltip-text">
                {{ $gettext('All: will import all your costs into a single invoice line') }}<br>
                {{ $gettext('On cost type: will import all your costs into one line per cost type') }}<br>
                {{ $gettext('No grouping: will import all costs in separate lines.') }}
              </div>
            </b-tooltip>

            <ImportAggregateCosts v-model="aggregationType" />
          </div>
        </template>
      </FilterBar>
      
    </div>
    <div class="selected-items-wrap">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">

        <b-table 
          :items="groupedItems"
          :fields="selectedFields"
          class="import-selected-items-table"
          :class="['gk-table gk-table--responsive gk-table--headed gk-table--striped']"
          show-empty
        >
          <template #empty="">
            {{ $gettext('No items selected to import')}}
          </template>
          <template #cell(description)="row">
            {{ row.item.description }}
          </template>
          <template #cell(total)="row">
            <span class="text-nowrap">
              {{ row.item.amount | formatNumber }}
            </span>
          </template>
        </b-table>

      </overlay-scrollbars>
    </div>
    <div class="import-actions-bar">
      <GekkoButton 
        @click="displayTable = !displayTable"
        :text="$gettext('Selected costs') + ' ' + '(' + selectedCosts.length + '/' + itemCount + ')'"
        :icon="(displayTable) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
        icon-right
        variant="link"
        class="px-0"
      />
      <div class="import-actions-bar--buttons">
        <div style="margin-right: 1rem; font-size: 14px;">
          {{ $gettext('Import a total of') }} {{ groupedItems.length }} {{ $gettext('lines') }}
        </div>
        <GekkoButton 
          @click="$emit('close', false)"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          @click="$emit('add-lines', groupedItems)"
          :text="(isQuotation) ? $gettext('Add to quotation') : $gettext('Add to invoice')"
        />
      </div>
    </div>
    <div class="available-items-wrap">
      <CostTable 
        ref="cost-table"
        :filter-bar="false"
        :component-on-modal="true" 
        :modal-temp-cookie="filterCookieName"
        :selectable-rows="true"
        :selected-return="'object'"
        :excludeTableFields="['created_by', 'completed']"
        :overlay-scrollb="false"
        @selected-item="selectedCostTableItems"
        @mounted="filtersChanged" 
        @destroyed="costTableDestroyed"
        @get-costs="determineCheckedCosts"
        extended
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import CostTable from "@/components/costs/CostTable";
import ImportAggregateCosts from "@/components/invoices/ui/ImportAggregateCosts";

export default {
  name: "ImportCostsToInvoice",
  components: {
    GekkoButton,
    LoadingSpinner,
    CostTable,
    FilterBar,
    ImportAggregateCosts
  },
  props: {
    filterCookieName: {
      type: String,
      default: ''
    },
    invoiceLines: {
      type: Array,
      default: () => []
    },
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      groupedItems: [],
      selectedCosts: [],
      costList: [],
      itemCount: 0,
      aggregationType: null,
      displayTable: false,
      initFilters: false,
      selectedFields: [
        { key: "description", label: this.$gettext('Description'), class: 'w-100' },
        { key: "total", label: this.$gettext('Amount (Ex. VAT)'), class: 'tablecol-sm text-right' },
      ]
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany"
    })
  },
  watch: {
    aggregationType () {
      this.groupBy()
    },
    selectedCosts () {
      this.groupBy()
    }
  },
  created () {
    this.groupBy()
  },
  mounted () {
    const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
    modalEl.addEventListener('scroll', this.handleScroll)
  },
  methods: {
    selectedCostTableItems () {
      this.refreshSelectedList()
    },
    costTableDestroyed () {
      const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
      if (modalEl) {
        modalEl.removeEventListener('scroll', this.handleScroll)
      }
    },
    determineCheckedCosts (item_count) {
      this.itemCount = item_count
      this.costList = this.$refs['cost-table'].costList

      let invoice_lines = []
      if (this.invoiceLines) {
        invoice_lines = this.invoiceLines;
      }

      for (let i = 0; i < this.costList.length; i++) {
        for (let j = 0; j < invoice_lines.length; j++) {
          if (invoice_lines[j].cost_ids && invoice_lines[j].cost_ids.indexOf(this.costList[i].id) != -1) {
            this.costList[i].is_invoiced = true
            break;
          }
        }
      }

      for (let i = 0; i < this.costList.length; i++) {
        if(!this.costList[i].is_invoiced && this.costList[i].customer) {
          this.costList[i].selected = !this.costList[i].is_invoiced
        }
      }
      this.refreshSelectedList()
    },
    refreshSelectedList () {
      this.selectedCosts = this.$refs['cost-table'].selectedTableItems
    },
    
    /**
     * Groups items and formats output for invoice lines 
     * invoice lines containing aggregated items
     * should have an array with correspondant item ids
     *    COSTS -> (array) invoice_line.cost_ids
     *    HOURS -> (array) invoice_line.aggregated_hours
     *    TRIPS -> (array) invoice_line.trip_ids
     *    PRODUCTS -> (number) invoice_line.product - cannot be grouped, just the ID
     */
    groupBy () {
      const items = this.selectedCosts

      console.log('groupBy items', items)
      const grouped_items = items.reduce((acc_items, item) => {
        // item[this.aggregationType] is now and object (extended query - need .id) or null
        const key = (!this.aggregationType || this.aggregationType === 'no') ? item.id : item[this.aggregationType]?.id 

        const existing_group = acc_items.find(group => group.grouped_by?.id === key);

        // Update group values
        if (existing_group) {

          // Increment amounts
          // existing_group.amount += parseFloat(item.amount)
          existing_group.amount += Math.round((parseFloat(item.amount) - parseFloat(item.total_tax)) * 100) / 100
          existing_group.total_tax += parseFloat(item.total_tax)

          // MULTIPLIER ?
          // vm.grouped_costs[key].multiplier = Math.round(vm.grouped_costs[key].multiplier*100)/100;

          // Save item id
          existing_group.cost_ids.push(item.id)

          // Set first and last date for item description
          existing_group.first_date = new Date(Math.min(new Date(existing_group.first_date), new Date(item.date))).toISOString().split('T')[0];
          existing_group.last_date = new Date(Math.max(new Date(existing_group.last_date), new Date(item.date))).toISOString().split('T')[0];
          
        } else {
          // Push a new group
          const group_first_item = {
            grouped_by: { type: this.aggregationType, id: key },
            cost_ids: [item.id],
            multiplier: 1.0,
            description: this.$gettext('Expenses') + ' ' + (new Date(item.date).toISOString().split('T')[0]) + ', ' + item.description,
            amount: Math.round((parseFloat(item.amount) - parseFloat(item.total_tax)) * 100) / 100,
            total_tax: parseFloat(item.total_tax),
            tax_rate: this.currentCompany.default_tax_rate,
            vat_exempt: false,
            first_date: item.date,
            last_date: item.date,
            cost_type: ''
          }
          if (this.aggregationType === 'cost_type') {
            group_first_item.cost_type = (item.cost_type) ? item.cost_type.name : ''
          }
          acc_items.push(group_first_item);
        }

        return acc_items;
      }, []);
      // Format the descriptions
      grouped_items.forEach(grouped_item => {
        if (this.aggregationType === 'all') {
          grouped_item.description = this.$gettext('Costs between') + ' ' + grouped_item.first_date + ' ' + this.$gettext('and') + ' ' + grouped_item.last_date
        } else if (this.aggregationType === 'cost_type') {
          grouped_item.description = grouped_item.cost_type + ' ' + this.$gettext('costs between') + ' ' + grouped_item.first_date + ' ' + this.$gettext('and') + ' ' + grouped_item.last_date
        }
        delete grouped_item.first_date;
        delete grouped_item.last_date;
      });
      this.groupedItems = grouped_items;

      console.log('this.groupedItems', this.groupedItems)
    },
    filtersChanged (value) {
      if (value) this.initFilters = value
      if (this.$refs['cost-table']) {
        this.$refs['cost-table'].filtersChanged(this.initFilters)
      }
    },
    handleScroll () {
      this.$refs['cost-table'].handleScroll()
    },
  }
};
</script>

<style lang="scss" scoped>

.invoice-import-costs {
  display: flex;
  flex-direction: column;
  // gap: 1rem;
  
  .import-filters {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.5rem;
  }
  .import-actions-bar {
    display: flex;
    justify-content: space-between;
    // gap: 0.5rem;
    align-items: center;
    padding: 1rem 0;
    &--buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      align-items: center;
    }
  }
  .selected-items-wrap {
    height: 300px;
  }
  &.hiddentable {
    // margin-bottom: -1rem;
    .import-actions-bar {
      padding-bottom: 0;
    }
    .available-items-wrap {
        height: 0;
        overflow: hidden;
    }
  }
}

</style>

<style lang="scss">

</style>